import { Action, createReducer, on } from '@ngrx/store';
import {
  Ship,
  ReportFormInterface,
  Report,
  DebugLog,
  BlendsResponse,
  BioBlend,
  ShipReportingRequirements,
} from 'src/types';
import {
  clearShip,
  loadReports,
  loadShip,
  loadBlends,
  createReport,
  updateReport,
  loadReportsFailure,
  loadShipFailure,
  createReportFailure,
  updateReportFailure,
  loadReportsSuccess,
  loadShipSuccess,
  setSelectedBunkerTypes,
  setCpid,
  setBioBlends,
  setReportState,
  setSelectedReport,
  setDebugLog,
  updateCurrentReport,
  loadBlendsSuccess,
  hydrateStore,
  setShipReportingRequirements,
} from './reporting.actions';

export interface State {
  reports: Report[];
  localReports: ReportFormInterface[];
  currentReport: ReportFormInterface;
  currentShip: Ship;
  currentShipBlends: BlendsResponse[];
  bioBlends: BioBlend[];
  selectedBunkerTypes: string[];
  error: unknown;
  log: DebugLog;
  loading: boolean;
  shipReportingRequirements: ShipReportingRequirements;
}

export const initialState: State = {
  reports: [],
  localReports: [] as ReportFormInterface[],
  currentReport: {} as ReportFormInterface,
  currentShip: {} as Ship,
  currentShipBlends: [] as BlendsResponse[],
  bioBlends: [] as BioBlend[],
  selectedBunkerTypes: [],
  error: null,
  log: {} as DebugLog,
  loading: false,
  shipReportingRequirements: {
    continuousReportingRequired: false,
    endOfMonthNoonSubmittedFor: null,
  },
};

export const reducerFn = createReducer(
  initialState,
  on(clearShip, (state, { external }): State => external
    ? { ...state, localReports: [], reports: [], currentReport: { ...state.currentReport,
      reportAccessToken: undefined, issueAccessToken: undefined } }
    : { ...initialState }),
  on(loadReports, loadBlends, createReport, updateReport, (state): State => {
    return { ...state, loading: true };
  }),
  on(loadShip, (state, { shipParameter }): State => {
    const shipReportingRequirements = shipParameter !== null && shipParameter !== state.currentShip.uuid
      ? {
          continuousReportingRequired: false,
          endOfMonthNoonSubmittedFor: null,
        }
      : state.shipReportingRequirements;
    return {
      ...state,
      bioBlends: [],
      shipReportingRequirements,
      loading: true,
    };
  }),
  on(loadReportsFailure, loadShipFailure, createReportFailure, updateReportFailure, (state, { error }): State => {
    return { ...state, error, loading: false };
  }),
  on(loadReportsSuccess, (state, { reports }): State => {
    return { ...state, localReports: reports, loading: false };
  }),
  on(loadShipSuccess, (state, { ship, requirements }): State => {
    const currentShip = { ...state.currentShip, ...ship };
    return { ...state, currentShip, shipReportingRequirements: requirements, loading: false };
  }),
  on(loadBlendsSuccess, (state, { blends }): State => {
    return { ...state, currentShipBlends: blends, loading: false };
  }),
  on(setBioBlends, (state, { bioBlends }): State => {
    return { ...state, bioBlends, loading: false };
  }),
  on(setReportState, (state, { reports }): State => {
    return { ...state, localReports: reports, loading: false };
  }),
  on(setSelectedBunkerTypes, (state, { selectedBunkerTypes }): State => {
    return { ...state, selectedBunkerTypes, loading: false };
  }),
  on(setSelectedReport, (state, { localId, reportType, reportDate, localTime, timezone: prefilledTimezone }): State => {
    const { localReports } = state;
    const timezone = prefilledTimezone ?? (localReports.length ? localStorage.getItem('timezone') || '' : '');

    const currentReport =
      localId === 'new'
        ? ({
            id: localId,
            reportType,
            timezone,
            reportDate: reportDate ?? new Date(),
            ...(localTime !== undefined ? { localTime } : {}),
          } as ReportFormInterface)
        : (state.localReports.find(({ localId: reportId }) => reportId === localId) as ReportFormInterface);
    return { ...state, currentReport };
  }),
  on(setShipReportingRequirements, (state, { requirements }): State => {
    return { ...state, shipReportingRequirements: requirements };
  }),
  on(setCpid, (state, { cpid }): State => {
    const currentShip = cpid ? { ...state.currentShip, cpid } : { ...state.currentShip };
    return { ...state, currentShip, loading: false };
  }),
  on(updateCurrentReport, (state, { report }): State => {
    return { ...state, currentReport: { ...state.currentReport, ...report } };
  }),
  on(setDebugLog, (state, { log }): State => {
    return { ...state, log, loading: false };
  }),
  on(hydrateStore, (state, { currentReport, selectedBunkerTypes }): State => {
    return { ...state, currentReport, selectedBunkerTypes };
  })
);

export function reducer(state = initialState, action: Action): State {
  return reducerFn(state, action);
}

export const getLocalReports = (state?: State) => state?.localReports ?? [];
export const getShip = (state?: State) => state?.currentShip ?? ({} as Ship);
export const getBioBlends = (state?: State) => state?.bioBlends ?? [];
export const getSelectedBunkerTypes = (state?: State) => state?.selectedBunkerTypes ?? [];
